import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";

import { useWorkflowById } from "../../../../reactQuery/workflows";
import Kanban from ".";
import Loader from "../../../../components/Loader";
import { useResponsive } from "../../../../hooks/useResponsive";
import MobileKanban from "./mobileKanban";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import { getResponseData } from "../../../../reactQuery/response";

function Workflows({ boardId, boardName, cards = [], loading = false, workflowId }) {
  const { data, isLoading } = useWorkflowById(workflowId, boardId);
  const isMobile = useResponsive("down", "md");
  const [searchParams] = useSearchParams();
  const [estimated] = useCustomSearchParams("estimated");
  const searchText = useSelector((state) => state.search.searchText);
  const epicSearch = useSelector((state) => state.epic.searchEpic);

  const searchParamsData = useMemo(
    () => ({
      filterUser: searchParams.get("search")?.split(",") || [],
      noMilestoneSearch: searchParams.get("noMilestone") === "true",
      cardId: searchParams.get("card_id"),
    }),
    [searchParams]
  );

  const workflowSteps = useMemo(() => {
    const workflowData = getResponseData(data, []);

    return [...workflowData].sort(
      (stepA, stepB) => stepA.step_sequence - stepB.step_sequence
    );
  }, [data]);

  const filteredCards = useMemo(() => {
    if (!cards.length) {
      return [];
    }

    let filteredResults = [...cards];

    if (searchParamsData.filterUser.length) {
      filteredResults = filteredResults.filter((cardData) =>
        searchParamsData.filterUser.includes(
          String(cardData?.card?.assigned_to_user_id)
        )
      );
    }

    if (epicSearch?.length) {
      filteredResults = filteredResults.filter((cardData) =>
        epicSearch.includes(cardData?.card?.epic_id)
      );
    }

    if (searchParamsData.noMilestoneSearch) {
      filteredResults = filteredResults.filter(
        (cardData) => cardData?.card?.epic_id == null
      );
    }

    if (searchText) {
      filteredResults = filteredResults.filter(
        (cardData) =>
          cardData?.card?.title?.toLowerCase().includes(searchText) ||
          cardData?.card?.assigned_to_user?.first_name
            ?.toLowerCase()
            .includes(searchText) ||
          cardData?.card?.assigned_to_user?.last_name
            ?.toLowerCase()
            .includes(searchText) ||
          cardData?.key?.toLowerCase().includes(searchText) ||
          cardData?.card?.card_id?.toString().toLowerCase().includes(searchText) ||
          cardData?.card?.epic?.epic_name?.toLowerCase().includes(searchText) ||
          cardData?.labels?.some((label) =>
            label?.toLowerCase().includes(searchText)
          )
      );
    }

    if (estimated === "Watching") {
      filteredResults = filteredResults.filter((card) => card?.card?.is_watch_list);
    } else if (estimated === "Estimated") {
      filteredResults = filteredResults.filter(
        (card) => card?.card?.allocated_hours
      );
    } else if (estimated === "Not Estimated") {
      filteredResults = filteredResults.filter(
        (card) => !card?.card?.allocated_hours
      );
    }

    return filteredResults;
  }, [cards, epicSearch, estimated, searchParamsData, searchText]);

  const sameWorkFlowIdcards = useMemo(() => {
    if (!searchParamsData.cardId || !filteredCards.length) {
      return [];
    }

    const currentCard = filteredCards.find(
      (item) => item?.card?.card_id === Number(searchParamsData.cardId)
    );
    const workflowStepId = currentCard?.card?.workflow_step_id;

    if (!workflowStepId) {
      return [];
    }

    return filteredCards.filter(
      (cardData) => cardData?.card?.workflow_step_id === workflowStepId
    );
  }, [filteredCards, searchParamsData.cardId]);

  if (loading || isLoading || !workflowSteps.length) {
    return (
      <div className="loader-center">
        <Loader color={"black"} />
      </div>
    );
  }

  return isMobile ? (
    <Box>
      <MobileKanban
        workflow={workflowSteps}
        data={filteredCards}
        cards={filteredCards}
        createCard={true}
      />
    </Box>
  ) : (
    <Kanban
      boardName={boardName}
      id={boardId}
      workflowSteps={workflowSteps}
      cards={filteredCards}
      sameWorkFlowIdcards={sameWorkFlowIdcards}
    />
  );
}

export default React.memo(Workflows);
